import { fetchMe, login as loginRequest } from "@/api/auth";
import { ApiError } from "@/api/client";
import { clearAll as clearApiCache } from "@/lib/api-cache";
import {
  UNAUTHORIZED_EVENT,
  clearSession,
  getStoredUser,
  getToken,
  setStoredUser,
  setToken,
} from "@/lib/auth";
import { markEnd, markStart } from "@/lib/performance";
import type { User } from "@/types/api";
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

type AuthStatus = "loading" | "authenticated" | "unauthenticated";

interface AuthContextValue {
  user: User | null;
  status: AuthStatus;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Provedor de autenticação. Hidrata o usuário do cache local para pintura
 * instantânea, revalida o token via `GET /me` e reage ao evento global de 401
 * (emitido pelo cliente HTTP) fazendo logout.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(() => (getToken() ? getStoredUser() : null));
  const [status, setStatus] = useState<AuthStatus>(() =>
    getToken() ? "loading" : "unauthenticated",
  );

  const logout = useCallback(() => {
    clearApiCache(); // zera o cache de buscas em memória
    clearSession();
    setUser(null);
    setStatus("unauthenticated");
  }, []);

  // Revalida o token ao montar.
  useEffect(() => {
    if (!getToken()) {
      setStatus("unauthenticated");
      return;
    }
    let active = true;
    fetchMe()
      .then((me) => {
        if (!active) {
          return;
        }
        setUser(me);
        setStoredUser(me);
        setStatus("authenticated");
      })
      .catch((error: unknown) => {
        if (!active) {
          return;
        }
        // 401 já limpou a sessão no cliente (e disparou o evento) — nada a fazer.
        if (error instanceof ApiError && error.status === 401) {
          return;
        }
        // Falha de rede/servidor: mantém a sessão em cache de forma otimista;
        // a próxima chamada que retornar 401 fará o logout.
        const cached = getStoredUser();
        if (cached) {
          setUser(cached);
          setStatus("authenticated");
        } else {
          logout();
        }
      });
    return () => {
      active = false;
    };
  }, [logout]);

  // Reage ao 401 global.
  useEffect(() => {
    const handleUnauthorized = () => logout();
    window.addEventListener(UNAUTHORIZED_EVENT, handleUnauthorized);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, handleUnauthorized);
  }, [logout]);

  const login = useCallback(async (email: string, password: string) => {
    markStart("login");
    const response = await loginRequest(email, password);
    setToken(response.token);
    setStoredUser(response.user);
    setUser(response.user);
    setStatus("authenticated");
    markEnd("login");
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, status, login, logout }),
    [user, status, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Hook de acesso ao contexto de autenticação. */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth deve ser usado dentro de <AuthProvider>.");
  }
  return context;
}
